/* eslint-disable react/forbid-dom-props */
import Image from 'next/future/image';
import { Dispatch, SetStateAction, useState } from 'react';
import cn from 'classnames';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import shieldIcon from '@/public/icons/buyback/sell-device/shield.svg';
import moneyBackIcon from '@/public/icons/buyback/money-back.svg';
import { LayoutProps, PageType } from '@/lib/buyback/types';
import ReebeloSatisfaction from './sell/SatisfactionModal';
import { BuybackUSConfig } from '@/settings/configs/buyback/us/type';
import { useBuybackContext } from '@/lib/buyback/BuybackContext';
import { priceFormater } from '@/settings/index';
import QuoteSummary from './sell/QuoteSummary';
import QuoteSummaryContent from './sell/QuoteSummaryContent';

const SatisfactionFooter = ({
  setShowModal,
}: {
  setShowModal: Dispatch<SetStateAction<boolean>>;
}) => (
  <>
    <div className="h-14 w-14 shrink-0">
      <Image
        src={shieldIcon}
        height={100}
        width={100}
        alt="Default"
        className="h-full w-full"
      />
    </div>
    <div>
      <h3 className="text-lg font-bold">Reebelo Satisfaction Promise</h3>
      <p className="mt-2 text-gray-700/70">
        As long as your device condition is described accurately, we will honor
        our quotation. Enjoy hassle-free payout, free shipping, expert data
        erasure, and free returns if you change your mind.{' '}
        <button
          className="font-bold text-[#0D6EFD] underline"
          onClick={() => setShowModal(true)}
        >
          That's our commitment
        </button>
        .
      </p>
    </div>
  </>
);

const MoneyFooter = ({
  setShowModal,
}: {
  setShowModal: Dispatch<SetStateAction<boolean>>;
}) => {
  const { summaryQuote } = useBuybackContext();

  if (summaryQuote == null) return null;

  return (
    <>
      <div className="relative h-16 shrink-0">
        <Image
          src={moneyBackIcon}
          height={100}
          width={100}
          alt="Default"
          className="h-full w-auto"
        />
        <span className="absolute top-[21px] left-[38px] rotate-[-16deg] text-base font-bold text-[#0C4B30]">
          ${summaryQuote.total.toLocaleString()}
        </span>
      </div>
      <div>
        <h3 className="text-lg font-bold">Your Money is Waiting for You</h3>
        <p className="mt-2 text-gray-700/70">
          The valuation of your {summaryQuote.model} has been completed, enter
          your informations to ship it.{' '}
          <button
            className="font-bold text-[#0D6EFD] underline"
            onClick={() => setShowModal(true)}
          >
            Show Quote Details
          </button>
          .
        </p>
      </div>
    </>
  );
};

const DeviceProgress = ({
  pageType,
  device,
  step,
  subStep,
  steps,
  setShowQuoteSummary,
  setShowSatisfactionModal,
}: {
  pageType: PageType;
  device: BuybackUSConfig;
  step: number;
  subStep: number;
  steps: number;
  setShowQuoteSummary: Dispatch<SetStateAction<boolean>>;
  setShowSatisfactionModal: Dispatch<SetStateAction<boolean>>;
}) => {
  const percentage = ((step + subStep) * 100) / (steps - 1);

  return (
    <>
      {pageType === PageType.NAVIGATION ? (
        <div className="absolute top-0 w-full">
          <Image
            src={getEdgeUrl('images/buyback/questionnaire/banner/default.png')}
            height={750}
            width={500}
            alt="Default"
            quality={100}
            loading="eager"
            className="hidden h-full w-full object-contain md:block"
          />
          <Image
            src={getEdgeUrl(
              'images/buyback/questionnaire/banner/default-mobile.png',
            )}
            height={1000}
            width={1000}
            alt="Default"
            quality={100}
            loading="eager"
            className="h-full w-full object-contain md:hidden"
          />
        </div>
      ) : (
        <div className="relative mb-5 h-full max-h-[500px] w-full flex-1">
          <Image
            src={getEdgeUrl(
              `images/buyback/questionnaire/banner/${device.category}.png`,
            )}
            height={1000}
            width={1000}
            alt="Default"
            quality={100}
            loading="eager"
            className="absolute left-1/2 h-full w-auto -translate-x-1/2 object-contain"
          />
          <div
            className="absolute top-0 w-full bg-gray-200 opacity-75 transition-all duration-150 ease-in-out"
            style={{
              height: `${100 - percentage}%`,
            }}
          />
        </div>
      )}
      <div className="relative flex w-full shrink-0 flex-col items-center gap-2 rounded-md bg-white p-4 shadow-lg xxs:flex-row xxs:gap-4">
        {[PageType.INFO, PageType.PAYOUT].includes(pageType) ? (
          <MoneyFooter setShowModal={setShowQuoteSummary} />
        ) : (
          <SatisfactionFooter setShowModal={setShowSatisfactionModal} />
        )}
      </div>
    </>
  );
};

const Summary = () => {
  const { summaryQuote } = useBuybackContext();

  if (summaryQuote == null) return null;

  return (
    <div className="w-full rounded-xl bg-white p-5">
      <div className="mx-auto h-[200px] w-full">
        <Image
          src={summaryQuote.image}
          alt="Device"
          height={400}
          width={400}
          className="h-full w-full object-contain"
        />
      </div>
      <div className="mt-10">
        <QuoteSummaryContent />
        {summaryQuote.total > 0 && (
          <div className="mt-5 flex items-center justify-between text-lg font-bold">
            <div>Total</div>
            <div>{priceFormater.short(summaryQuote.total)}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const LeftSide = ({
  pageType,
  device,
  step,
  subStep,
  steps,
}: {
  pageType: PageType;
  device: BuybackUSConfig;
  step: number;
  subStep: number;
  steps: number;
}) => {
  const { summaryQuote } = useBuybackContext();
  const [showSatisfactionModal, setShowSatisfactionModal] = useState(false);
  const [showQuoteSummary, setShowQuoteSummary] = useState(false);

  return (
    <>
      <div
        className={cn(
          'sticky top-[122px] flex min-h-[500px] w-full flex-col items-center overflow-hidden bg-gray-200 p-5 md:w-[50%] lg:w-[40%] lg:px-10 3xl:w-[50%]',
          {
            'justify-end': pageType === PageType.NAVIGATION,
            'justify-center xl-h:md:max-h-[calc(100vh-122px)]':
              pageType === PageType.VALUATION,
            'md:max-h-[calc(100vh-122px)]': pageType !== PageType.VALUATION,
          },
        )}
      >
        {![PageType.VALUATION].includes(pageType) ||
        (summaryQuote != null && !summaryQuote.model) ? (
          <DeviceProgress
            pageType={pageType}
            device={device}
            step={step}
            subStep={subStep}
            steps={steps}
            setShowQuoteSummary={setShowQuoteSummary}
            setShowSatisfactionModal={setShowSatisfactionModal}
          />
        ) : (
          <Summary />
        )}
      </div>
      <ReebeloSatisfaction
        showModal={showSatisfactionModal}
        setShowModal={setShowSatisfactionModal}
      />
      <QuoteSummary
        showModal={showQuoteSummary}
        setShowModal={setShowQuoteSummary}
      />
    </>
  );
};

const RightSide = ({ children }: { children: JSX.Element }) => (
  <div className="flex w-full items-center justify-center bg-white p-5 md:w-[50%] lg:w-[60%] lg:p-10 3xl:w-[50%]">
    <div className="h-full w-full max-w-[750px]">{children}</div>
  </div>
);

const Layout = (props: LayoutProps) => {
  const {
    device,
    pageType,
    step = 1,
    steps = 1,
    subStep = 0,
    children,
  } = props;

  return (
    <div className="relative h-full w-screen">
      <div className="relative z-10 mx-auto flex h-full max-w-[1280px] flex-col-reverse bg-gray-200 md:min-h-[calc(100vh-122px)] md:flex-row">
        <LeftSide
          device={device}
          pageType={pageType}
          step={step}
          steps={steps}
          subStep={subStep}
        />
        <RightSide>{children}</RightSide>
      </div>
      <div className="absolute left-0 top-0 z-0 hidden h-full w-full md:flex">
        <div className="w-full bg-gray-200 md:w-1/2 lg:min-h-[100%] lg:w-[40%] lg:px-10 3xl:w-[50%]" />
        <div className="w-full bg-white md:w-[50%] lg:w-[60%] lg:p-10 3xl:w-[50%]" />
      </div>
    </div>
  );
};

export default Layout;
